import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// Custom shimmer skeleton with animation
const ShimmerSkeleton = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "rounded-md bg-muted relative overflow-hidden",
      "before:absolute before:inset-0 before:-translate-x-full",
      "before:animate-[shimmer_2s_infinite]",
      "before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
      className
    )}
    {...props}
  />
);

interface AnimatedSectionProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

const AnimatedSection = ({ children, delay = 0, className }: AnimatedSectionProps) => (
  <div
    className={cn("animate-fade-in opacity-0", className)}
    style={{ 
      animationDelay: `${delay}ms`,
      animationFillMode: 'forwards'
    }}
  >
    {children}
  </div>
);

const ResultsSkeleton = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header Skeleton */}
        <AnimatedSection delay={0}>
          <Card className="p-8 shadow-2xl border-2 border-primary/20">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <ShimmerSkeleton className="w-20 h-20 rounded-full" />
              <div className="flex-1 space-y-3">
                <ShimmerSkeleton className="h-10 w-3/4" />
                <ShimmerSkeleton className="h-5 w-1/2" />
              </div>
              <ShimmerSkeleton className="h-10 w-32 rounded-full" />
            </div>
          </Card>
        </AnimatedSection>

        {/* Action Buttons Skeleton */}
        <AnimatedSection delay={100}>
          <Card className="p-6 border-primary/10">
            <div className="flex flex-wrap gap-3 justify-center">
              <ShimmerSkeleton className="h-10 w-40" />
              <ShimmerSkeleton className="h-10 w-40" />
              <ShimmerSkeleton className="h-10 w-40" />
              <ShimmerSkeleton className="h-10 w-40" />
            </div>
          </Card>
        </AnimatedSection>

        {/* Tier Justification Skeleton */}
        <AnimatedSection delay={200}>
          <Card className="p-8 border-primary/10">
            <ShimmerSkeleton className="h-7 w-48 mb-4" />
            <ShimmerSkeleton className="h-20 w-full" />
          </Card>
        </AnimatedSection>

        {/* Profile Cards Skeleton */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Assessment Profile */}
          <AnimatedSection delay={300}>
            <Card className="p-6 border-primary/10 h-full">
              <div className="flex items-center gap-3 mb-6">
                <ShimmerSkeleton className="w-10 h-10 rounded-full" />
                <ShimmerSkeleton className="h-6 w-40" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <ShimmerSkeleton className="h-3 w-20" />
                    <ShimmerSkeleton className="h-5 w-full" />
                  </div>
                ))}
              </div>
            </Card>
          </AnimatedSection>

          {/* Psychological Profile */}
          <AnimatedSection delay={400}>
            <Card className="p-6 border-primary/10 h-full">
              <div className="flex items-center gap-3 mb-6">
                <ShimmerSkeleton className="w-10 h-10 rounded-full" />
                <ShimmerSkeleton className="h-6 w-44" />
              </div>
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <ShimmerSkeleton className="h-4 w-32" />
                    <ShimmerSkeleton className="h-5 w-full" />
                  </div>
                ))}
              </div>
            </Card>
          </AnimatedSection>
        </div>

        {/* Strategy Elements Skeleton */}
        <AnimatedSection delay={500}>
          <Card className="p-6 border-primary/10">
            <ShimmerSkeleton className="h-7 w-48 mb-6" />
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-4 rounded-lg bg-muted/30 space-y-3">
                  <div className="flex justify-between items-center">
                    <ShimmerSkeleton className="h-6 w-48" />
                    <ShimmerSkeleton className="h-6 w-16 rounded-full" />
                  </div>
                  <ShimmerSkeleton className="h-4 w-full" />
                  <ShimmerSkeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          </Card>
        </AnimatedSection>

        {/* Accordion Sections Skeleton */}
        <AnimatedSection delay={600}>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4 border-primary/10">
                <div className="flex items-center gap-3">
                  <ShimmerSkeleton className="w-8 h-8 rounded" />
                  <ShimmerSkeleton className="h-6 w-56" />
                </div>
              </Card>
            ))}
          </div>
        </AnimatedSection>

        {/* Timeline Skeleton */}
        <AnimatedSection delay={700}>
          <Card className="p-6 border-primary/10">
            <ShimmerSkeleton className="h-7 w-56 mb-6" />
            <div className="grid md:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-4 rounded-lg bg-muted/30 space-y-3">
                  <ShimmerSkeleton className="h-5 w-24" />
                  <ShimmerSkeleton className="h-4 w-full" />
                  <ShimmerSkeleton className="h-4 w-3/4" />
                  <ShimmerSkeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          </Card>
        </AnimatedSection>

        {/* Back Button Skeleton */}
        <AnimatedSection delay={800}>
          <div className="flex justify-center pt-4">
            <ShimmerSkeleton className="h-12 w-48" />
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
};

export default ResultsSkeleton;
